/**
 * Analytics API Client
 * 
 * Phase 8.0: Analytics & Insights
 * Handles all analytics-related API calls to the backend.
 */

import { getShopifySessionToken, getCachedHost, getAppBridgeInstance } from "../utils/app-bridge";

// Use empty string for same-origin requests (production) or localhost for dev
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

/**
 * Wait for App Bridge to be initialized (with timeout)
 */
async function waitForAppBridge(maxWaitMs = 5000): Promise<void> {
  const startTime = Date.now();
  while (!getAppBridgeInstance()) {
    if (Date.now() - startTime > maxWaitMs) {
      throw new Error("Timeout waiting for App Bridge initialization");
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Wait for App Bridge to be ready before getting token
  await waitForAppBridge();
  
  const sessionToken = await getShopifySessionToken();

  // Get host parameter from cache (persists across navigation)
  const hostParam = getCachedHost();
  
  // Append host parameter to endpoint if present (required in production)
  let fullEndpoint = `${API_BASE_URL}${endpoint}`;
  if (hostParam) {
    const separator = endpoint.includes("?") ? "&" : "?";
    fullEndpoint += `${separator}host=${encodeURIComponent(hostParam)}`;
  }

  const response = await fetch(fullEndpoint, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || error.message || "API request failed");
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// ============================================================================
// Types (from backend/src/types/analytics.ts)
// ============================================================================

export interface OverviewMetrics {
  totalPlays: number;
  uniquePlayers: number;
  rewardsGranted: number;
  conversionRate: number;
  avgSessionDuration: number;
  rewardsRejected: number;
}

export interface CampaignMetrics {
  campaignId: string;
  campaignName: string;
  totalPlays: number;
  uniquePlayers: number;
  rewardsGranted: number;
  conversionRate: number;
  avgSessionDuration: number;
  rewardsRejected: number;
  outcomeDistribution: { outcomeKey: string; count: number; percentage: number }[];
  recentActivity: { timestamp: string; eventType: string; outcomeKey?: string }[];
}

export interface TimeSeriesEntry {
  time: string;
  plays: number;
  rewards: number;
  conversionRate: number;
}

export type TimeSeriesData = TimeSeriesEntry[];

export type TimeSeriesGranularity = 'hour' | 'day' | 'week' | 'month';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get analytics overview for the shop
 */
export async function getAnalyticsOverview(): Promise<OverviewMetrics> {
  return apiRequest<OverviewMetrics>("/admin/analytics/overview");
}

/**
 * Get detailed analytics for a specific campaign
 */
export async function getCampaignAnalytics(campaignId: string): Promise<CampaignMetrics> {
  return apiRequest<CampaignMetrics>(`/admin/analytics/campaigns/${campaignId}`);
}

/**
 * Get time-series data for plays and rewards
 */
export async function getTimeSeriesData(
  granularity: TimeSeriesGranularity = 'day',
  days: number = 30
): Promise<TimeSeriesData> {
  return apiRequest<TimeSeriesData>(
    `/admin/analytics/timeseries?granularity=${granularity}&days=${days}`
  );
}
